import { useState, useRef, useEffect, useCallback } from 'react';
import { useArtistSearch } from '../hooks/useArtistSearch';
import { useAuth, useAuthActions, useAuthDispatch } from '../state/AuthContext';
import { getUserTopArtists } from '../api/lastfmClient';
import { searchArtists } from '../api/deezerClient';
import * as authApi from '../api/authClient';
import '../styles/favorites.css';

const IMPORT_LIMITS = [25, 50, 100, 250, 500];

function AddFavoriteModal({ onClose }) {
  const { query, setQuery, results, isLoading, clearResults } = useArtistSearch();
  const { favorites } = useAuth();
  const { toggleFavorite } = useAuthActions();
  const dispatch = useAuthDispatch();
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef(null);
  const overlayRef = useRef(null);

  // Last.fm import state
  const [showImport, setShowImport] = useState(false);
  const [lastfmUsername, setLastfmUsername] = useState('');
  const [importLimit, setImportLimit] = useState(100);
  const [importState, setImportState] = useState('idle'); // idle | fetching | importing | done | error
  const [importError, setImportError] = useState('');
  const [importProgress, setImportProgress] = useState({ imported: 0, skipped: 0, total: 0 });
  const [importPreview, setImportPreview] = useState(null); // { newArtists, skippedCount }
  const importAbortRef = useRef(false);

  const favoriteNames = new Set(favorites.map((f) => f.artistName));

  useEffect(() => {
    if (!showImport) {
      inputRef.current?.focus();
    }
  }, [showImport]);

  useEffect(() => {
    setHighlightIndex(-1);
  }, [results]);

  // Cleanup abort on unmount
  useEffect(() => {
    return () => { importAbortRef.current = true; };
  }, []);

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      if (showImport) {
        if (importState === 'importing') return; // don't close during import
        setShowImport(false);
        setImportState('idle');
        setImportPreview(null);
        setImportError('');
        return;
      }
      if (query) {
        clearResults();
      } else {
        onClose();
      }
      return;
    }

    if (results.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      handleSelect(results[highlightIndex]);
    }
  }

  function handleSelect(artist) {
    toggleFavorite(artist.name, artist.id, artist.image || artist.imageLarge || null);
  }

  function handleOverlayClick(e) {
    if (e.target === overlayRef.current) {
      onClose();
    }
  }

  // Fetch top artists from Last.fm and show preview
  const handleFetchTopArtists = useCallback(async () => {
    if (!lastfmUsername.trim()) return;

    setImportState('fetching');
    setImportError('');
    setImportPreview(null);

    try {
      const topArtists = await getUserTopArtists(lastfmUsername.trim(), importLimit, 'overall');

      if (topArtists.length === 0) {
        setImportError('No top artists found for this user.');
        setImportState('idle');
        return;
      }

      // Current favorites at time of fetch
      const currentFavNames = new Set(favorites.map((f) => f.artistName.toLowerCase().trim()));
      const newArtists = topArtists.filter(
        (a) => !currentFavNames.has(a.name.toLowerCase().trim())
      );
      const skippedCount = topArtists.length - newArtists.length;

      setImportPreview({ newArtists, skippedCount, totalFetched: topArtists.length });
      setImportState('preview');
    } catch (err) {
      if (err.code === 'USER_NOT_FOUND') {
        setImportError('Last.fm user not found. Check the username and try again.');
      } else {
        setImportError('Failed to fetch top artists. Please try again.');
      }
      setImportState('idle');
    }
  }, [lastfmUsername, importLimit, favorites]);

  // Import the previewed artists
  const handleConfirmImport = useCallback(async () => {
    if (!importPreview || importPreview.newArtists.length === 0) return;

    const artists = importPreview.newArtists;
    setImportState('importing');
    setImportProgress({ imported: 0, skipped: 0, total: artists.length });
    importAbortRef.current = false;

    let imported = 0;
    let skipped = 0;

    for (const artist of artists) {
      if (importAbortRef.current) break;

      try {
        // Try to get a Deezer image for the artist
        let artistId = null;
        let artistImage = null;
        try {
          const deezerResults = await searchArtists(artist.name, 1, { priority: false });
          if (deezerResults.length > 0 && deezerResults[0].name.toLowerCase().trim() === artist.name.toLowerCase().trim()) {
            artistId = deezerResults[0].id;
            artistImage = deezerResults[0].image;
          }
        } catch {
          // Skip enrichment, import with name only
        }

        await authApi.addFavorite({
          artistName: artist.name,
          artistId: artistId,
          artistImage: artistImage,
        });
        imported++;
      } catch {
        // 409 (duplicate) or other error — skip
        skipped++;
      }

      setImportProgress({ imported, skipped, total: artists.length });
    }

    setImportState('done');
  }, [importPreview]);

  // After import is done, refresh favorites from server to sync state
  useEffect(() => {
    if (importState === 'done') {
      authApi.getFavorites()
        .then((res) => res.json())
        .then((data) => {
          if (data.favorites) {
            dispatch({ type: 'SET_FAVORITES', favorites: data.favorites });
            dispatch({ type: 'SET_UNIVERSE_STATUS', status: 'stale' });
          }
        })
        .catch(() => {});
    }
  }, [importState, dispatch]);

  function renderImportView() {
    if (importState === 'preview' && importPreview) {
      return (
        <div className="lastfm-import-content">
          <div className="lastfm-import-preview">
            <div className="lastfm-import-stats">
              <span className="import-stat">
                <strong>{importPreview.newArtists.length}</strong> new artist{importPreview.newArtists.length !== 1 ? 's' : ''} to import
              </span>
              {importPreview.skippedCount > 0 && (
                <span className="import-stat import-stat-skipped">
                  {importPreview.skippedCount} already favorited
                </span>
              )}
            </div>
            {importPreview.newArtists.length > 0 && (
              <>
                <div className="lastfm-import-artist-list">
                  {importPreview.newArtists.map((a, i) => (
                    <div key={i} className="lastfm-import-artist-row">
                      <span className="lastfm-import-artist-name">{a.name}</span>
                      <span className="lastfm-import-artist-plays">
                        {a.playcount.toLocaleString()} plays
                      </span>
                    </div>
                  ))}
                </div>
                <button
                  className="lastfm-import-confirm-btn"
                  onClick={handleConfirmImport}
                >
                  Import {importPreview.newArtists.length} artist{importPreview.newArtists.length !== 1 ? 's' : ''}
                </button>
              </>
            )}
            {importPreview.newArtists.length === 0 && (
              <div className="lastfm-import-all-exist">
                All top artists are already in your favorites!
              </div>
            )}
            <button
              className="lastfm-import-back-btn"
              onClick={() => { setImportState('idle'); setImportPreview(null); }}
            >
              Back
            </button>
          </div>
        </div>
      );
    }

    if (importState === 'importing') {
      const { imported, skipped, total } = importProgress;
      const done = imported + skipped;
      const pct = total > 0 ? Math.round((done / total) * 100) : 0;
      return (
        <div className="lastfm-import-content">
          <div className="lastfm-import-progress">
            <div className="import-progress-bar-track">
              <div className="import-progress-bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="import-progress-text">
              Importing... {done} of {total}
            </div>
            {imported > 0 && (
              <span className="import-stat">{imported} imported</span>
            )}
            {skipped > 0 && (
              <span className="import-stat import-stat-skipped">{skipped} skipped</span>
            )}
          </div>
        </div>
      );
    }

    if (importState === 'done') {
      const { imported, skipped } = importProgress;
      return (
        <div className="lastfm-import-content">
          <div className="lastfm-import-done">
            <div className="import-done-message">
              Import complete!
            </div>
            <div className="import-done-stats">
              {imported > 0 && <span className="import-stat">{imported} imported</span>}
              {skipped > 0 && <span className="import-stat import-stat-skipped">{skipped} skipped</span>}
            </div>
            <button className="lastfm-import-back-btn" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      );
    }

    // idle / fetching state — show form
    return (
      <div className="lastfm-import-content">
        <div className="lastfm-import-form">
          <input
            type="text"
            className="lastfm-import-username"
            placeholder="Last.fm username"
            value={lastfmUsername}
            onChange={(e) => setLastfmUsername(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && lastfmUsername.trim()) {
                e.preventDefault();
                handleFetchTopArtists();
              }
              if (e.key === 'Escape') {
                setShowImport(false);
                setImportError('');
              }
            }}
            autoFocus
            disabled={importState === 'fetching'}
          />
          <div className="lastfm-import-limit-row">
            <span className="lastfm-import-limit-label">Top artists:</span>
            <div className="lastfm-import-limit-options">
              {IMPORT_LIMITS.map((n) => (
                <button
                  key={n}
                  className={`lastfm-import-limit-btn ${importLimit === n ? 'active' : ''}`}
                  onClick={() => setImportLimit(n)}
                  disabled={importState === 'fetching'}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          {importError && <div className="lastfm-import-error">{importError}</div>}
          <button
            className="lastfm-import-fetch-btn"
            onClick={handleFetchTopArtists}
            disabled={!lastfmUsername.trim() || importState === 'fetching'}
          >
            {importState === 'fetching' ? (
              <>
                <div className="search-spinner" />
                Fetching...
              </>
            ) : (
              'Fetch Top Artists'
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="galaxy-info-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="add-fav-modal">
        <button className="galaxy-info-close" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <h3 className="add-fav-title">Add Favorites</h3>

        {!showImport ? (
          <>
            <div className="add-fav-search-wrapper">
              <svg
                className="search-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                className="add-fav-search-input"
                placeholder="Search for an artist..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              {isLoading && <div className="search-spinner" />}
            </div>

            <div className="add-fav-results">
              {results.length > 0 && results.map((artist, index) => {
                const isFav = favoriteNames.has(artist.name);
                return (
                  <button
                    key={artist.id}
                    className={`add-fav-result ${index === highlightIndex ? 'highlighted' : ''} ${isFav ? 'is-favorite' : ''}`}
                    onClick={() => handleSelect(artist)}
                    onMouseEnter={() => setHighlightIndex(index)}
                  >
                    {artist.image ? (
                      <img className="result-image" src={artist.image} alt={artist.name} />
                    ) : (
                      <div className="result-image result-image-placeholder">
                        {artist.name.charAt(0)}
                      </div>
                    )}
                    <div className="result-info">
                      <span className="result-name">{artist.name}</span>
                      {artist.genres && artist.genres.length > 0 && (
                        <span className="result-genres">
                          {artist.genres.slice(0, 2).join(', ')}
                        </span>
                      )}
                    </div>
                    {isFav && (
                      <svg className="add-fav-star" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                      </svg>
                    )}
                  </button>
                );
              })}
              {isLoading && results.length === 0 && (
                <div className="add-fav-loading">Searching...</div>
              )}
              {!isLoading && query.length >= 2 && results.length === 0 && (
                <div className="add-fav-loading">No results found</div>
              )}
              {!query && (
                <div className="add-fav-hint">Search for artists to add them to your favorites</div>
              )}
            </div>

            {!query && !isLoading && (
              <button
                className="lastfm-import-toggle-btn"
                onClick={() => setShowImport(true)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <path d="M12 5v14M5 12l7-7 7 7" />
                </svg>
                Import from Last.fm
              </button>
            )}
          </>
        ) : (
          renderImportView()
        )}
      </div>
    </div>
  );
}

export default AddFavoriteModal;
